import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, Package, EyeOff, Eye, ArrowUp, ArrowDown, TrendingUp, TrendingDown, Circle, Ban, FlaskConical, RotateCcw, Store } from 'lucide-react';
import ProductEditor from './ProductEditor';

const API = process.env.REACT_APP_BACKEND_URL;

function ProductRow({ product, onEdit, onDelete, onMoveUp, onMoveDown, onStatusChange, isFirst, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const categoryColors = {
    apparel: 'text-purple-400 bg-purple-400/10',
    drinkware: 'text-blue-400 bg-blue-400/10',
    bags: 'text-green-400 bg-green-400/10',
    stationery: 'text-amber-400 bg-amber-400/10',
    bundles: 'text-rose-400 bg-rose-400/10',
  };

  return (
    <div data-testid="product-row" className="bg-[#111111] border border-white/5 rounded-xl hover:border-white/10 transition-colors">
      <div className="p-3 sm:p-4">
        {/* Top row: move arrows + image + info */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex flex-col gap-0.5 flex-shrink-0">
            <button onClick={onMoveUp} disabled={isFirst} data-testid={`move-up-${product.slug}`}
              className="w-6 h-6 rounded flex items-center justify-center text-slate-600 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-20 disabled:pointer-events-none">
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <button onClick={onMoveDown} disabled={isLast} data-testid={`move-down-${product.slug}`}
              className="w-6 h-6 rounded flex items-center justify-center text-slate-600 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-20 disabled:pointer-events-none">
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          </div>

          {(() => {
            const views = product.views || [];
            const colors = product.color_variants || [];
            const viewId = views[0]?.id;
            const imgPath = (viewId && colors[0]?.images?.[viewId]) || views[0]?.image;
            return imgPath ? (
              <img src={`${API}/api/files/${imgPath}`} alt={product.title}
                className="w-10 h-10 rounded-lg object-cover border border-white/10 flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <Package className="w-4 h-4 text-slate-600" />
              </div>
            );
          })()}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              {product.category && (
                <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${categoryColors[product.category] || 'text-slate-400 bg-slate-400/10'}`}>
                  {product.category}
                </span>
              )}
              {product.status === 'hidden' && (
                <span className="text-[9px] font-bold uppercase tracking-widest text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <EyeOff className="w-2.5 h-2.5" /> Hidden
                </span>
              )}
              {product.status === 'sold_out' && (
                <span className="text-[9px] font-bold uppercase tracking-widest text-rose-400 bg-rose-400/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Ban className="w-2.5 h-2.5" /> Sold Out
                </span>
              )}
            </div>
            <h3 className="font-['Outfit'] text-sm font-semibold text-white truncate">{product.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">£{product.base_price.toFixed(2)}</p>
          </div>

          <button onClick={() => setExpanded(!expanded)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* Action buttons — separate row on mobile */}
        <div className="flex items-center gap-1 mt-2 ml-8 sm:ml-[52px]">
          {product.status === 'hidden' ? (
            <button data-testid={`show-product-${product.slug}`} onClick={() => onStatusChange(product.id, 'active')} title="Show on shop"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-orange-400 hover:text-green-400 hover:bg-green-500/10 transition-colors">
              <Eye className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button data-testid={`hide-product-${product.slug}`} onClick={() => onStatusChange(product.id, 'hidden')} title="Hide from shop"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-orange-400 hover:bg-orange-400/10 transition-colors">
              <EyeOff className="w-3.5 h-3.5" />
            </button>
          )}
          {product.status === 'sold_out' ? (
            <button data-testid={`restock-product-${product.slug}`} onClick={() => onStatusChange(product.id, 'active')} title="Mark as available"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-rose-400 hover:text-green-400 hover:bg-green-500/10 transition-colors">
              <Ban className="w-3.5 h-3.5" />
            </button>
          ) : product.status !== 'hidden' && (
            <button data-testid={`soldout-product-${product.slug}`} onClick={() => onStatusChange(product.id, 'sold_out')} title="Mark as sold out"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-400/10 transition-colors">
              <Ban className="w-3.5 h-3.5" />
            </button>
          )}
          <button data-testid="product-edit-btn" onClick={() => onEdit(product)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button data-testid="product-delete-btn" onClick={() => onDelete(product.id)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-white/5 pt-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-slate-500">Views:</span>
              <span className="text-slate-300 ml-1">{(product.views || []).map(v => v.name).join(', ') || 'None'}</span>
            </div>
            <div>
              <span className="text-slate-500">Colours:</span>
              <span className="text-slate-300 ml-1">{(product.color_variants || []).map(c => c.name).join(', ') || 'None'}</span>
            </div>
          </div>
          {product.sub_options?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Options</span>
              {product.sub_options.map((group, i) => (
                <div key={i} className="text-xs text-slate-400">
                  <span className="text-slate-300 font-medium">{group.name}:</span>{' '}
                  {group.options?.map((o, j) => (
                    <span key={j}>
                      {o.label}{o.price_modifier > 0 ? ` (+£${o.price_modifier})` : ''}
                      {j < group.options.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [shopVisible, setShopVisible] = useState(true);

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetch(`${API}/api/shop/settings`).then(r => r.json()).then(d => {
      setTestMode(!!d.test_mode);
      if (d.shop_visible === false) setShopVisible(false);
    }).catch(() => {});
  }, [fetchProducts]);

  const toggleTestMode = async () => {
    const newVal = !testMode;
    setTestMode(newVal);
    const token = localStorage.getItem('admin_token');
    await fetch(`${API}/api/admin/shop/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ test_mode: newVal }),
    });
  };

  const toggleShopVisible = async () => {
    const newVal = !shopVisible;
    setShopVisible(newVal);
    const token = localStorage.getItem('admin_token');
    await fetch(`${API}/api/admin/shop/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ shop_visible: newVal }),
    });
  };

  const handleBulkPriceAdjust = async (percent) => {
    const direction = percent > 0 ? 'increase' : 'decrease';
    if (!window.confirm(`${direction === 'increase' ? 'Increase' : 'Decrease'} ALL product prices by ${Math.abs(percent)}%?`)) return;
    setAdjusting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/products/bulk-price-adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ percent }),
      });
      if (!res.ok) throw new Error('Failed');
      await fetchProducts();
    } catch (err) {
      console.error('Bulk price adjust failed:', err);
    } finally {
      setAdjusting(false);
    }
  };

  const handleBulkPriceRound = async () => {
    if (!window.confirm('Round ALL prices to nearest .99?')) return;
    setAdjusting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/products/bulk-price-round`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed');
      await fetchProducts();
    } catch (err) {
      console.error('Bulk price round failed:', err);
    } finally {
      setAdjusting(false);
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Reset ALL product prices to their default values?')) return;
    setAdjusting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/products/bulk-price-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed');
      await fetchProducts();
    } catch (err) {
      console.error('Reset defaults failed:', err);
    } finally {
      setAdjusting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    const token = localStorage.getItem('admin_token');
    await fetch(`${API}/api/admin/products/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setProducts(p => p.filter(prod => prod.id !== id));
  };

  const handleSave = (savedProduct) => {
    if (editing?.id) {
      setProducts(p => p.map(prod => prod.id === savedProduct.id ? savedProduct : prod));
    } else {
      setProducts(p => [savedProduct, ...p]);
    }
    setEditing(null);
  };

  const handleMove = async (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= products.length) return;
    const reordered = [...products];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setProducts(reordered);
    const token = localStorage.getItem('admin_token');
    const order = reordered.map(p => p.id);
    try {
      await fetch(`${API}/api/admin/products/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order }),
      });
    } catch (err) {
      console.error('Failed to save order:', err);
    }
  };

  const handleStatusChange = async (productId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API}/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      setProducts(p => p.map(prod => prod.id === productId ? { ...prod, status: newStatus } : prod));
    } catch (err) {
      console.error('Status change failed:', err);
    }
  };

  if (editing !== null) {
    return <ProductEditor product={editing.id ? editing : null} onSave={handleSave} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-['Outfit'] text-xl font-bold text-white">Products</h2>
            <p className="text-sm text-slate-500 mt-0.5">{products.length} product{products.length !== 1 ? 's' : ''}</p>
          </div>
          <button data-testid="product-new-btn" onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-white transition-colors">
            <Plus className="w-4 h-4" /> New Product
          </button>
        </div>

        {/* Controls row — wraps on mobile */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center border border-white/10 rounded-xl overflow-hidden" data-testid="bulk-price-controls">
            <button data-testid="price-decrease-btn" onClick={() => handleBulkPriceAdjust(-5)} disabled={adjusting}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors disabled:opacity-50 border-r border-white/10">
              <TrendingDown className="w-3.5 h-3.5" /> -5%
            </button>
            <button data-testid="price-increase-btn" onClick={() => handleBulkPriceAdjust(5)} disabled={adjusting}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-400 hover:text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50 border-r border-white/10">
              <TrendingUp className="w-3.5 h-3.5" /> +5%
            </button>
            <button data-testid="price-round-btn" onClick={handleBulkPriceRound} disabled={adjusting}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-400 hover:text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors disabled:opacity-50">
              <Circle className="w-3.5 h-3.5" /> .99
            </button>
          </div>

          <button data-testid="price-reset-default-btn" onClick={handleResetDefaults} disabled={adjusting}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-white/10 text-slate-400 hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
            <RotateCcw className="w-3.5 h-3.5" /> Default
          </button>

          <button data-testid="shop-visible-toggle" onClick={toggleShopVisible}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${shopVisible ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400' : 'border-rose-500/50 bg-rose-500/10 text-rose-400'}`}>
            <Store className="w-3.5 h-3.5" /> {shopVisible ? 'Shop Live' : 'Shop Hidden'}
          </button>

          <button data-testid="test-mode-toggle" onClick={toggleTestMode}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${testMode ? 'border-amber-500/50 bg-amber-500/10 text-amber-400' : 'border-white/10 text-slate-500 hover:text-amber-400 hover:border-amber-500/30'}`}>
            <FlaskConical className="w-3.5 h-3.5" /> {testMode ? 'Test Mode ON' : 'Test Mode'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Loading products...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
          <Package className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No products yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((product, idx) => (
            <ProductRow key={product.id} product={product}
              onEdit={(p) => setEditing(p)} onDelete={handleDelete}
              onStatusChange={handleStatusChange}
              onMoveUp={() => handleMove(idx, -1)} onMoveDown={() => handleMove(idx, 1)}
              isFirst={idx === 0} isLast={idx === products.length - 1} />
          ))}
        </div>
      )}
    </div>
  );
}
